import { type ReactNode, useEffect, useState } from "react";
import { fetchCentralLead } from "../api/whatsapp.js";
import {
  CentralLeadDetailSections,
  CentralLeadFieldGrid,
  CentralLeadInsightCards,
} from "./central-lead/CentralLeadSections.tsx";
import { buildCentralLeadView } from "./central-lead/customFieldView.ts";
import type { CentralLead } from "./central-lead/types.ts";

export type { CentralLead } from "./central-lead/types.ts";

type FetchState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "found"; lead: CentralLead }
  | { kind: "absent"; reason: string };

export function shouldRenderLeadFallback(state: { kind: string }): boolean {
  return state.kind !== "found";
}

const CACHE_TTL_MS = 60 * 60 * 1000;
const STORAGE_PREFIX = "wcrm.central-lead.v1:";
const cache = new Map<string, FetchState>();

function loadFromStorage(chatJid: string): FetchState | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + chatJid);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as { ts: number; state: FetchState };
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL_MS) {
      localStorage.removeItem(STORAGE_PREFIX + chatJid);
      return null;
    }
    return parsed.state;
  } catch {
    return null;
  }
}

function saveToStorage(chatJid: string, state: FetchState): void {
  try {
    if (state.kind === "loading" || state.kind === "idle") return;
    localStorage.setItem(STORAGE_PREFIX + chatJid, JSON.stringify({ ts: Date.now(), state }));
  } catch {
    /* localStorage full: ignore silently */
  }
}

function statusColor(status: string): string {
  const map: Record<string, string> = {
    new: "#10b981",
    enriched: "#06b6d4",
    contacted: "#3b82f6",
    qualified: "#8b5cf6",
    won: "#16a34a",
    lost: "#9ca3af",
    invalid: "#ef4444",
  };
  return map[status?.toLowerCase()] || "#6b7280";
}

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    new: "Novo",
    enriched: "Enriquecido",
    contacted: "Em contato",
    qualified: "Qualificado",
    won: "Ganho",
    lost: "Perdido",
    invalid: "Invalido",
  };
  return map[status?.toLowerCase()] || status || "-";
}

export function CentralLeadCard({
  chatJid,
  isGroup,
  onLeadFound,
  fallback = null,
}: {
  chatJid: string;
  isGroup: boolean;
  onLeadFound?: (lead: CentralLead) => void;
  fallback?: ReactNode;
}) {
  const [state, setState] = useState<FetchState>(() => cache.get(chatJid) || { kind: "idle" });
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    if (isGroup) {
      setState({ kind: "absent", reason: "group" });
      return;
    }

    const memHit = cache.get(chatJid);
    if (memHit && memHit.kind !== "idle") {
      setState(memHit);
      if (memHit.kind === "found") onLeadFound?.(memHit.lead);
      return;
    }

    const storageHit = loadFromStorage(chatJid);
    if (storageHit) {
      cache.set(chatJid, storageHit);
      setState(storageHit);
      if (storageHit.kind === "found") onLeadFound?.(storageHit.lead);
      return;
    }

    setState({ kind: "loading" });
    let cancelled = false;
    fetchCentralLead(chatJid)
      .then((data: { found: boolean; lead: CentralLead | null; reason?: string }) => {
        if (cancelled) return;
        const next: FetchState = data?.found && data.lead
          ? { kind: "found", lead: data.lead }
          : { kind: "absent", reason: data?.reason || "not_in_central" };
        cache.set(chatJid, next);
        saveToStorage(chatJid, next);
        setState(next);
        if (next.kind === "found") onLeadFound?.(next.lead);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const next: FetchState = { kind: "absent", reason: "error" };
        cache.set(chatJid, next);
        setState(next);
        console.warn("[wcrm] central lead lookup failed", err);
      });
    return () => { cancelled = true; };
  }, [chatJid, isGroup, onLeadFound]);

  if (state.kind !== "found") return fallback;

  const lead = state.lead;
  const fullName = lead.fullName || [lead.firstName, lead.lastName].filter(Boolean).join(" ") || lead.email || "Lead";
  const score = typeof lead.score === "number" ? lead.score : null;
  const view = buildCentralLeadView(lead);

  return (
    <div className={`central-lead-card ${expanded ? "is-expanded" : ""}`}>
      <header className="central-lead-head">
        <div className="central-lead-title">
          <span
            className="central-lead-status"
            style={{ background: statusColor(lead.status) }}
            title={`Status: ${statusLabel(lead.status)}`}
          >
            {statusLabel(lead.status)}
          </span>
          <strong className="central-lead-name">{fullName}</strong>
          {score !== null ? <span className="central-lead-score" title="Score">Score {score}</span> : null}
        </div>
        <div className="central-lead-actions">
          {lead.centralUrl ? (
            <a className="central-lead-link" href={lead.centralUrl} target="_blank" rel="noopener noreferrer">
              Ver na Central
            </a>
          ) : null}
          <button
            type="button"
            className="central-lead-toggle"
            onClick={() => setExpanded((value) => !value)}
            aria-label={expanded ? "Recolher dados do lead" : "Expandir dados do lead"}
          >
            {expanded ? "Recolher" : "Mais dados"}
          </button>
        </div>
      </header>

      <CentralLeadFieldGrid fields={view.summaryFields} />
      <CentralLeadInsightCards cards={view.insightCards} />

      {expanded ? (
        <CentralLeadDetailSections
          qualificationFields={view.qualificationFields}
          detailSections={view.detailSections}
          hiddenSensitiveCount={view.hiddenSensitiveCount}
        />
      ) : null}
    </div>
  );
}
